import { useState, useRef, useEffect, useCallback } from "react";
import { Trash2, GripVertical, Pencil } from "lucide-react";

/**
 * PDFAnnotationLayer - Canvas overlay for PDF page annotations
 * 
 * Handles:
 * - Drawing strokes (freehand)
 * - Text annotation positioning with direct manipulation
 * - Eraser functionality
 * - Touch and mouse input
 * - Smooth pointer-based dragging
 * 
 * Uses normalized coordinates (0-1) for responsive scaling.
 */
export default function PDFAnnotationLayer({
  pageNumber, // 0-indexed
  pageWidth,
  pageHeight,
  annotations, // Annotations for this page only
  activeTool,
  strokeWidth,
  strokeColor = "#000000",
  fontSize,
  onAddAnnotation,
  onUpdateAnnotation,
  onDeleteAnnotation,
  selectedAnnotationId,
  onSelectAnnotation,
  onStartTextEdit,
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentStroke, setCurrentStroke] = useState([]);
  
  // Drag state for smooth text movement
  const [dragState, setDragState] = useState(null);
  // { annotationId, startPos, annotationStartPos, offset, isDragging }
  
  const rafRef = useRef(null);
  const lastClickTimeRef = useRef(0);
  const lastClickIdRef = useRef(null);

  // Get pointer position in normalized coordinates (0-1)
  const getPointerPosition = useCallback((e) => {
    const container = containerRef.current;
    if (!container) return null;

    const rect = container.getBoundingClientRect();
    let clientX, clientY;

    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if (e.changedTouches && e.changedTouches.length > 0) {
      clientX = e.changedTouches[0].clientX;
      clientY = e.changedTouches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    // Clamp to page bounds
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));

    return { x, y };
  }, []);

  // Redraw canvas for drawing strokes only
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw all drawing annotations
    const drawings = annotations.filter(a => a.type === "drawing");
    for (const drawing of drawings) {
      if (!drawing.points || drawing.points.length < 2) continue;

      ctx.beginPath();
      ctx.strokeStyle = drawing.stroke_color || "#000000";
      ctx.lineWidth = (drawing.stroke_width || 2) * (canvas.width / 800);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      const firstPoint = drawing.points[0];
      ctx.moveTo(firstPoint.x * canvas.width, firstPoint.y * canvas.height);

      for (let i = 1; i < drawing.points.length; i++) {
        const point = drawing.points[i];
        ctx.lineTo(point.x * canvas.width, point.y * canvas.height);
      }

      ctx.stroke();
    }

    // Draw current stroke (while drawing)
    if (currentStroke.length > 1) {
      ctx.beginPath();
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeWidth * (canvas.width / 800);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      ctx.moveTo(currentStroke[0].x * canvas.width, currentStroke[0].y * canvas.height);
      for (let i = 1; i < currentStroke.length; i++) {
        ctx.lineTo(currentStroke[i].x * canvas.width, currentStroke[i].y * canvas.height);
      }
      ctx.stroke();
    }
  }, [annotations, currentStroke, strokeColor, strokeWidth]);

  // Resize canvas to match container
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const resizeObserver = new ResizeObserver(() => {
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;
      redrawCanvas();
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [redrawCanvas]);

  // Redraw when annotations change
  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  // Check if point is near an annotation (for eraser/selection)
  const findAnnotationAtPoint = useCallback((pos) => {
    // Check text annotations first (easier to hit)
    const texts = annotations.filter(a => a.type === "text");
    for (const text of texts) {
      const textWidth = text.width || 0.15;
      const textHeight = text.height || 0.04;
      
      if (
        pos.x >= text.x - 0.005 &&
        pos.x <= text.x + textWidth + 0.005 &&
        pos.y >= text.y - 0.005 &&
        pos.y <= text.y + textHeight + 0.005
      ) {
        return text;
      }
    }

    // Check drawing strokes (within threshold)
    const drawings = annotations.filter(a => a.type === "drawing");
    const threshold = 0.02;
    
    for (const drawing of drawings) {
      if (!drawing.points) continue;
      
      for (const point of drawing.points) {
        const dist = Math.sqrt(
          Math.pow(pos.x - point.x, 2) + Math.pow(pos.y - point.y, 2)
        );
        if (dist < threshold) {
          return drawing;
        }
      }
    }

    return null;
  }, [annotations]);

  // Handle text annotation click (with double-click detection)
  const handleTextAnnotationClick = useCallback((annotation, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    const isSameAnnotation = lastClickIdRef.current === annotation.id;
    
    // Double-click detection (300ms threshold)
    if (isSameAnnotation && timeSinceLastClick < 300) {
      // Double-click: edit text
      onStartTextEdit(annotation);
      lastClickTimeRef.current = 0;
      lastClickIdRef.current = null;
    } else {
      // Single click: select
      onSelectAnnotation(annotation.id);
      lastClickTimeRef.current = now;
      lastClickIdRef.current = annotation.id;
    }
  }, [onSelectAnnotation, onStartTextEdit]);

  // Handle text annotation pointer down (start drag)
  const handleTextPointerDown = useCallback((annotation, e) => {
    // Don't start drag in Draw or Eraser mode
    if (activeTool === "draw" || activeTool === "eraser") return;
    
    e.stopPropagation();
    e.preventDefault();
    
    const pos = getPointerPosition(e);
    if (!pos) return;

    // Calculate offset from annotation origin to click point
    const offsetX = pos.x - annotation.x;
    const offsetY = pos.y - annotation.y;

    // Select the annotation
    onSelectAnnotation(annotation.id);

    // Set up drag state
    setDragState({
      annotationId: annotation.id,
      startPos: pos,
      annotationStartPos: { x: annotation.x, y: annotation.y },
      offset: { x: offsetX, y: offsetY },
      isDragging: false,
      currentPos: { x: annotation.x, y: annotation.y },
    });

    // Capture pointer for smooth tracking
    if (e.target.setPointerCapture && e.pointerId !== undefined) {
      e.target.setPointerCapture(e.pointerId);
    }
  }, [activeTool, getPointerPosition, onSelectAnnotation]);

  // Handle pointer move during text drag
  const handleTextPointerMove = useCallback((e) => {
    if (!dragState) return;
    
    e.preventDefault();
    
    const pos = getPointerPosition(e);
    if (!pos) return;

    // Check if we've moved enough to consider it a drag (5px threshold)
    const container = containerRef.current;
    if (container && !dragState.isDragging) {
      const rect = container.getBoundingClientRect();
      const pixelMovement = Math.sqrt(
        Math.pow((pos.x - dragState.startPos.x) * rect.width, 2) +
        Math.pow((pos.y - dragState.startPos.y) * rect.height, 2)
      );
      
      if (pixelMovement < 5) return;
    }

    // Calculate new position using offset to prevent jumping
    const newX = pos.x - dragState.offset.x;
    const newY = pos.y - dragState.offset.y;

    // Clamp to page boundaries (keep at least 10% of annotation visible)
    const clampedX = Math.max(-0.1, Math.min(0.95, newX));
    const clampedY = Math.max(-0.02, Math.min(0.95, newY));

    // Update drag state with RAF for smooth rendering
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      setDragState(prev => ({
        ...prev,
        isDragging: true,
        currentPos: { x: clampedX, y: clampedY },
      }));
    });
  }, [dragState, getPointerPosition]);

  // Handle pointer up (end drag)
  const handleTextPointerUp = useCallback((e) => {
    if (!dragState) return;
    
    e.preventDefault();

    // Release pointer capture
    if (e.target.releasePointerCapture && e.pointerId !== undefined) {
      try {
        e.target.releasePointerCapture(e.pointerId);
      } catch (err) {
        // Ignore release errors
      }
    }

    // Cancel any pending RAF
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    // If we were actually dragging, save the final position
    if (dragState.isDragging) {
      onUpdateAnnotation(dragState.annotationId, {
        x: dragState.currentPos.x,
        y: dragState.currentPos.y,
      });
    }

    setDragState(null);
  }, [dragState, onUpdateAnnotation]);

  // Global keyboard handler for delete
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedAnnotationId && (e.key === "Delete" || e.key === "Backspace")) {
        // Don't delete if user is typing in an input
        if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
        
        e.preventDefault();
        onDeleteAnnotation(selectedAnnotationId);
      }
      
      if (e.key === "Escape") {
        onSelectAnnotation(null);
        setDragState(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedAnnotationId, onDeleteAnnotation, onSelectAnnotation]);

  // Handle canvas pointer down (for drawing/erasing/clicking empty space)
  const handleCanvasPointerDown = (e) => {
    // If clicking on canvas (not a text annotation), handle based on tool
    const pos = getPointerPosition(e);
    if (!pos) return;

    if (activeTool === "draw") {
      e.preventDefault();
      setIsDrawing(true);
      setCurrentStroke([pos]);
    } else if (activeTool === "eraser") {
      const annotation = findAnnotationAtPoint(pos);
      if (annotation) {
        onDeleteAnnotation(annotation.id);
      }
    } else if (activeTool === "text") {
      // Check if clicking existing text to edit
      const annotation = findAnnotationAtPoint(pos);
      if (annotation && annotation.type === "text") {
        onStartTextEdit(annotation);
      } else {
        // Create new text annotation at click position
        const newId = crypto.randomUUID();
        onAddAnnotation({
          id: newId,
          page: pageNumber,
          type: "text",
          x: pos.x,
          y: pos.y,
          text: "",
          font_size: fontSize,
        });
        // Keep the new annotation selected after creation
        onSelectAnnotation(newId);
      }
    } else if (activeTool === "select") {
      // Click on empty space deselects
      const annotation = findAnnotationAtPoint(pos);
      if (!annotation) {
        onSelectAnnotation(null);
      }
    }
  };

  // Handle canvas pointer move (for drawing)
  const handleCanvasPointerMove = (e) => {
    if (activeTool === "draw" && isDrawing) {
      e.preventDefault();
      const pos = getPointerPosition(e);
      if (pos) {
        setCurrentStroke(prev => [...prev, pos]);
      }
    } else if (activeTool === "eraser" && e.buttons === 1) {
      const pos = getPointerPosition(e);
      if (pos) {
        const annotation = findAnnotationAtPoint(pos);
        if (annotation) {
          onDeleteAnnotation(annotation.id);
        }
      }
    }
  };

  // Handle canvas pointer up (for drawing)
  const handleCanvasPointerUp = () => {
    if (activeTool === "draw" && isDrawing && currentStroke.length > 1) {
      onAddAnnotation({
        id: crypto.randomUUID(),
        page: pageNumber,
        type: "drawing",
        x: 0,
        y: 0,
        points: currentStroke,
        stroke_width: strokeWidth,
        stroke_color: strokeColor,
      });
    }

    setIsDrawing(false);
    setCurrentStroke([]);
  };

  // Touch event handlers
  const handleTouchStart = (e) => {
    if (activeTool === "draw") {
      e.preventDefault();
    }
    handleCanvasPointerDown(e);
  };

  const handleTouchMove = (e) => {
    if (activeTool === "draw" && isDrawing) {
      e.preventDefault();
    }
    handleCanvasPointerMove(e);
  };

  // Get cursor style based on tool and hover state
  const getCursorStyle = () => {
    if (activeTool === "draw") return "crosshair";
    if (activeTool === "text") return "text";
    if (activeTool === "eraser") return "not-allowed";
    return "default";
  };

  // Get text annotation style based on drag state
  const getTextAnnotationStyle = (annotation) => {
    const isSelected = selectedAnnotationId === annotation.id;
    const isDragging = dragState?.annotationId === annotation.id && dragState.isDragging;
    
    // Use drag position if currently dragging this annotation
    const posX = isDragging ? dragState.currentPos.x : annotation.x;
    const posY = isDragging ? dragState.currentPos.y : annotation.y;

    return {
      position: "absolute",
      left: `${posX * 100}%`,
      top: `${posY * 100}%`,
      fontSize: `${annotation.font_size || 14}px`,
      lineHeight: 1.2,
      minWidth: "20px",
      minHeight: "1.2em",
      padding: "2px 6px",
      backgroundColor: annotation.text ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.7)",
      border: isSelected ? "2px solid #205B5A" : (annotation.text ? "1px solid transparent" : "1px dashed #999"),
      borderRadius: "2px",
      cursor: isDragging ? "grabbing" : (activeTool === "draw" ? "crosshair" : "grab"),
      userSelect: "none",
      touchAction: "none",
      boxShadow: isSelected ? "0 2px 8px rgba(32, 91, 90, 0.15)" : "none",
      transition: isDragging ? "none" : "border-color 0.15s, box-shadow 0.15s",
      transform: isDragging ? "translate3d(0,0,0)" : undefined, // Force GPU layer during drag
      zIndex: isDragging ? 1000 : (isSelected ? 100 : 1),
    };
  };

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 z-10"
      style={{ 
        cursor: getCursorStyle(),
        touchAction: activeTool === "draw" ? "none" : "auto",
      }}
      data-testid={`pdf-annotation-layer-page-${pageNumber}`}
    >
      {/* Drawing canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ pointerEvents: activeTool === "draw" || activeTool === "eraser" ? "auto" : "none" }}
        onMouseDown={handleCanvasPointerDown}
        onMouseMove={handleCanvasPointerMove}
        onMouseUp={handleCanvasPointerUp}
        onMouseLeave={handleCanvasPointerUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleCanvasPointerUp}
      />

      {/* Text annotations (rendered as DOM elements for direct manipulation) */}
      {annotations
        .filter(a => a.type === "text")
        .map(annotation => {
          const isSelected = selectedAnnotationId === annotation.id;
          const isDragging = dragState?.annotationId === annotation.id && dragState.isDragging;

          return (
            <div
              key={annotation.id}
              className="pointer-events-auto select-none"
              style={getTextAnnotationStyle(annotation)}
              onClick={(e) => handleTextAnnotationClick(annotation, e)}
              onPointerDown={(e) => handleTextPointerDown(annotation, e)}
              onPointerMove={handleTextPointerMove}
              onPointerUp={handleTextPointerUp}
              onPointerCancel={handleTextPointerUp}
              onDoubleClick={(e) => {
                e.stopPropagation();
                onStartTextEdit(annotation);
              }}
              data-testid={`text-annotation-${annotation.id}`}
            >
              {/* Annotation content */}
              <span style={{ pointerEvents: "none" }}>
                {annotation.text || (
                  <span className="text-[#999] italic text-sm">Click to edit</span>
                )}
              </span>

              {/* Selection controls */}
              {isSelected && !isDragging && annotation.text && (
                <div 
                  className="absolute -top-7 left-0 flex items-center gap-1 bg-white rounded shadow-md border border-gray-200 px-1 py-0.5"
                  style={{ pointerEvents: "auto", zIndex: 1001 }}
                >
                  <button
                    className="p-1 hover:bg-gray-100 rounded text-gray-600 hover:text-[#205B5A] transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      onStartTextEdit(annotation);
                    }}
                    title="Edit text"
                    data-testid={`edit-annotation-${annotation.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    className="p-1 hover:bg-red-50 rounded text-gray-600 hover:text-red-500 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteAnnotation(annotation.id);
                    }}
                    title="Delete (or press Delete key)"
                    data-testid={`delete-annotation-${annotation.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          );
        })}

      {/* Click handler for empty canvas area (select/text tool) */}
      {(activeTool === "select" || activeTool === "text") && (
        <div
          className="absolute inset-0"
          style={{ 
            pointerEvents: "auto", 
            zIndex: 0,
            cursor: activeTool === "text" ? "text" : "default",
          }}
          onClick={handleCanvasPointerDown}
        />
      )}
    </div>
  );
}
